import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResolvedCustomField } from '@tailoring-crm/shared';
import { Input, Textarea, Label } from '@tailoring-crm/ui';

/**
 * One custom field's input, rendered from its type, so any module's form (the customer dialog
 * first) can ask for a value the same way.
 *
 * The value is a STRING whatever the type, because a form state of strings is one state to
 * reason about; `lib/custom-fields/values.ts` owns the conversion to the typed wire slots.
 */

export interface CustomValueInputProps {
  field: ResolvedCustomField;
  /** `id` for the control, so the caller owns uniqueness across its form. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  error?: ReactElement | null;
  describedBy?: string | undefined;
  invalid?: boolean;
}

export function CustomValueInput({
  field,
  id,
  value,
  onChange,
  error = null,
  describedBy,
  invalid = false,
}: CustomValueInputProps): ReactElement | null {
  const { t } = useTranslation();
  if (field.type === null) return null;

  const label = (field.label ?? field.defaultLabel) + (field.required ? ' *' : '');

  if (field.type === 'boolean') {
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium">
          <input
            id={id}
            type="checkbox"
            checked={value === 'yes'}
            onChange={(event) => onChange(event.target.checked ? 'yes' : '')}
            className="size-4 rounded border-input"
          />
          {field.label ?? field.defaultLabel}
        </label>
        {field.helperText ? (
          <p className="text-sm text-muted-foreground">{field.helperText}</p>
        ) : null}
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      {field.type === 'select' ? (
        <select
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">{t('customFields.notChosen')}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.type === 'long_text' ? (
        <Textarea
          id={id}
          rows={3}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={describedBy}
        />
      ) : (
        <Input
          id={id}
          type={field.type === 'date' ? 'date' : 'text'}
          inputMode={field.type === 'money' || field.type === 'number' ? 'decimal' : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : undefined}
        />
      )}
      {field.helperText ? (
        <p className="text-sm text-muted-foreground">{field.helperText}</p>
      ) : null}
      {error}
    </div>
  );
}
